import { useCallback, useMemo } from 'react';

import { CAST_AI } from '@cast/constants';
import { Icons } from '@cast/design-system';
import { useClusters } from '@cast/shared-cluster-context-data-access';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';
import { Organization, OrganizationType } from '@cast/types';

import {
  buildEnterpriseUrl,
  buildManagementUrl,
  getEnterpriseIdFromOrg,
  useOrganization,
  useSetViewedOrganizationId,
  useViewedOrganization,
} from '@cast/infrastructure-organization';
import { SUPPORTS_COMMITMENTS } from 'constants/cluster';
import { useAbility } from '@cast/shared-ability-data-access';
import { useIsEnterpriseCommitmentsEnabled } from 'features/commitments/hooks';
import { useIsViewingEnterpriseOrg } from 'features/organization/hooks/useIsViewingEnterpriseOrg';
import { useIsHideDiscountsFeatureEnabled } from 'hooks/permissions';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useOrgHasClustersWithProvider } from 'hooks/useOrgHasClustersWithProvider';

import { SidebarItem } from '../_components/SidebarItem';
import { SidebarLayout } from '../_components/SidebarLayout';
import { SidebarSection } from '../_components/SidebarSection';
import { ViewByOrganizationSelector } from '../_components/ViewByOrganizationSelector';
import { useOrganizationSettingsQuery } from '@cast/infrastructure-abilities';

export const SettingsSidebar = () => {
  const { currentOrganization, organizations } = useOrganizations();
  const canViewEnterprise = useAbility().can('view', 'EnterpriseManagement');
  const isEnterpriseCommitmentsEnabled = useIsEnterpriseCommitmentsEnabled();
  const { anywhereOnly } = useClusters();

  // Use new viewed organization hooks
  const viewedOrganization = useViewedOrganization();
  const limitAutomationConsole = useShouldLimitConsoleForReporting(
    viewedOrganization?.id
  );
  const setViewedOrganizationId = useSetViewedOrganizationId();

  const handleSetViewedOrganization = useCallback(
    (org: Organization) => {
      setViewedOrganizationId(org.id);
    },
    [setViewedOrganizationId]
  );

  // Use viewedOrganization to determine what to display
  const viewedEnterpriseId = getEnterpriseIdFromOrg(viewedOrganization);
  const isViewingEnterpriseOrg = useIsViewingEnterpriseOrg();
  const isViewingChildOrg = viewedOrganization?.type === OrganizationType.CHILD;

  const { data: orgSettings } = useOrganizationSettingsQuery({
    organizationId: viewedOrganization?.id,
  });

  // Enterprise org for VIEW BY - use derivedEnterpriseId to find the enterprise org
  const enterpriseOrg = useOrganization(viewedEnterpriseId);

  // Child orgs under this enterprise - use derivedEnterpriseId to filter child orgs
  const childOrgs = useMemo(
    () =>
      organizations?.filter(
        (org) =>
          org.type === OrganizationType.CHILD &&
          org.parentId === viewedEnterpriseId
      ) || [],
    [organizations, viewedEnterpriseId]
  );

  // Include enterprise org in picker options if user has view permission
  const organizationOptions = [
    ...(enterpriseOrg && canViewEnterprise ? [enterpriseOrg] : []),
    ...childOrgs,
  ].map((org) => ({
    ...org,
    selected: org.id === currentOrganization?.id,
  }));

  const hideDiscounts = useIsHideDiscountsFeatureEnabled();

  const hasCommitmentClusters = useOrgHasClustersWithProvider([
    ...SUPPORTS_COMMITMENTS,
  ]);

  // Compute tooltip messages for disabled items
  const commitmentsTooltip =
    !hasCommitmentClusters || anywhereOnly
      ? anywhereOnly
        ? `Not available for ${CAST_AI} Anywhere clusters.`
        : 'No clusters with commitment support (EKS, GKE, or AKS).'
      : undefined;

  // Show VIEW BY selector if:
  // 1. User has enterprise context (from URL OR derived from child orgs), AND
  // 2. There are multiple organizations available to switch between
  // The ViewByOrganizationSelector component will handle filtering based on permissions
  const showViewBySelector =
    !!viewedEnterpriseId && organizationOptions.length > 1;

  return (
    <SidebarLayout
      title="Settings"
      subtitle="Organization settings"
      icon={<Icons.GearSix size={20} weight="light" />}
    >
      <>
        {/* Add VIEW BY selector at the top */}
        {showViewBySelector && (
          <ViewByOrganizationSelector
            viewedOrganization={viewedOrganization}
            onViewedOrganizationChange={handleSetViewedOrganization}
          />
        )}
        {isViewingEnterpriseOrg ? (
          <SidebarSection title="Enterprise">
            <SidebarItem
              title="Overview"
              url={buildEnterpriseUrl(viewedEnterpriseId!, 'overview')}
              allowedInReadOnlyOrg
            />
            <SidebarItem
              title="Organizations"
              url={buildEnterpriseUrl(
                viewedEnterpriseId!,
                'child-organizations'
              )}
            />
            <SidebarItem
              title="Users"
              url={buildEnterpriseUrl(viewedEnterpriseId!, 'manage-users')}
            />
            <SidebarItem
              title="User groups"
              url={buildEnterpriseUrl(
                viewedEnterpriseId!,
                'manage-user-groups'
              )}
            />
            <SidebarItem
              title="Single sign-on"
              url={buildEnterpriseUrl(viewedEnterpriseId!, 'sso-connections')}
            />
            <SidebarItem
              title="Service accounts"
              url={buildEnterpriseUrl(
                viewedEnterpriseId!,
                'manage-service-accounts'
              )}
            />
            <SidebarItem
              title="Platform usage"
              url={buildEnterpriseUrl(
                viewedEnterpriseId!,
                'enterprise-billing-report'
              )}
              activeMatchUrls={[
                buildEnterpriseUrl(viewedEnterpriseId!, 'billing-report'),
                buildManagementUrl('billing-report'),
              ]}
            />
            {isEnterpriseCommitmentsEnabled && !limitAutomationConsole && (
              <SidebarItem
                title="Commitments"
                url={buildEnterpriseUrl(viewedEnterpriseId!, 'commitments')}
              />
            )}
            <SidebarItem
              title="Feature control"
              url={buildEnterpriseUrl(viewedEnterpriseId!, 'feature-control')}
              rules={[['view', 'FeatureOrganizationManagementFeatureControl']]}
            />
          </SidebarSection>
        ) : isViewingChildOrg ? (
          <>
            <SidebarSection title="Organization">
              <SidebarItem
                title="Overview"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'dashboard',
                  viewedOrganization?.id
                )}
              />
              <SidebarItem
                title="Platform usage"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'org-billable-cpus',
                  viewedOrganization?.id
                )}
                activeMatchUrls={[
                  buildEnterpriseUrl(viewedEnterpriseId!, 'billing-report'),
                  buildManagementUrl('billing-report'),
                ]}
              />
            </SidebarSection>

            <SidebarSection title="Access control">
              <SidebarItem
                title="Users"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'access-control/users',
                  viewedOrganization?.id
                )}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              <SidebarItem
                title="User groups"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'access-control/user-groups',
                  viewedOrganization?.id
                )}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              {orgSettings?.enterpriseMultipleSso && (
                <SidebarItem
                  title="Single sign-on"
                  url={buildEnterpriseUrl(
                    viewedEnterpriseId!,
                    'child-sso-connections',
                    viewedOrganization?.id
                  )}
                  rules={[['view', 'OrganizationSsoConnections']]}
                  rulesConditions={{ organizationId: viewedOrganization?.id }}
                />
              )}
              <SidebarItem
                title="Service accounts"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'access-control/service-accounts',
                  viewedOrganization?.id
                )}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              <SidebarItem
                title="Role bindings"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'access-control/role-bindings',
                  viewedOrganization?.id
                )}
                rules={[
                  ['view', 'OrganizationAccessManagement'],
                  ['view', 'FeatureOrganizationManagementRoleBindings'],
                ]}
              />
              <SidebarItem
                title="Resources"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'access-control/resources',
                  viewedOrganization?.id
                )}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
            </SidebarSection>

            <SidebarSection title="System">
              <SidebarItem
                title="Cloud integrations"
                url={buildEnterpriseUrl(
                  viewedEnterpriseId!,
                  'cloud-integrations',
                  viewedOrganization?.id
                )}
                rules={[['view', 'OrganizationCloudIntegrations']]}
              />
              {!hideDiscounts && !limitAutomationConsole && (
                <SidebarItem
                  title="Commitments"
                  url={buildEnterpriseUrl(
                    viewedEnterpriseId!,
                    'commitments',
                    viewedOrganization?.id
                  )}
                  disabled={!hasCommitmentClusters || anywhereOnly}
                  rules={[['view', 'OrganizationCommitments']]}
                  supportsDemo={false}
                  tooltip={commitmentsTooltip}
                />
              )}
              {!limitAutomationConsole && (
                <SidebarItem
                  title="Component control"
                  url={buildEnterpriseUrl(
                    viewedEnterpriseId!,
                    'component-control',
                    viewedOrganization?.id
                  )}
                  rules={[
                    ['view', 'FeatureOrganizationManagementComponentControl'],
                  ]}
                />
              )}
              {!limitAutomationConsole && (
                <SidebarItem
                  title="Permissions center"
                  url={buildEnterpriseUrl(
                    viewedEnterpriseId!,
                    'permissions-center',
                    viewedOrganization?.id
                  )}
                />
              )}
              {!hideDiscounts && (
                <SidebarItem
                  title="Price adjustments"
                  url={buildEnterpriseUrl(
                    viewedEnterpriseId!,
                    'discount-engine',
                    viewedOrganization?.id
                  )}
                  rules={[['view', 'OrganizationDiscounts']]}
                  rulesConditions={{ organizationId: viewedOrganization?.id }}
                  supportsDemo={false}
                />
              )}
            </SidebarSection>
          </>
        ) : (
          <>
            <SidebarSection title="Organization">
              <SidebarItem
                title="Overview"
                url={buildManagementUrl('dashboard')}
              />
              <SidebarItem
                title="Platform usage"
                url={buildManagementUrl('billing-report')}
                rules={[['view', 'OrganizationBillingReport']]}
                rulesConditions={{ organizationId: currentOrganization?.id }}
              />
            </SidebarSection>

            <SidebarSection title="Access control">
              <SidebarItem
                title="Users"
                url={buildManagementUrl('access-control/users')}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              <SidebarItem
                title="User groups"
                url={buildManagementUrl('access-control/user-groups')}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              <SidebarItem
                title="Single sign-on"
                url={buildManagementUrl('sso-connections')}
                rules={[['view', 'OrganizationSsoConnections']]}
              />
              <SidebarItem
                title="Service accounts"
                url={buildManagementUrl('access-control/service-accounts')}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
              <SidebarItem
                title="Role bindings"
                url={buildManagementUrl('access-control/role-bindings')}
                rules={[
                  ['view', 'OrganizationAccessManagement'],
                  ['view', 'FeatureOrganizationManagementRoleBindings'],
                ]}
              />
              <SidebarItem
                title="Resources"
                url={buildManagementUrl('access-control/resources')}
                rules={[['view', 'OrganizationAccessManagement']]}
              />
            </SidebarSection>

            <SidebarSection title="System">
              <SidebarItem
                title="Cloud integrations"
                url={buildManagementUrl('cloud-integrations')}
                rules={[['view', 'OrganizationCloudIntegrations']]}
              />
              <SidebarItem
                title="Feature control"
                url={buildManagementUrl('feature-control')}
                rules={[
                  ['view', 'FeatureOrganizationManagementFeatureControl'],
                ]}
              />
              {!hideDiscounts && !limitAutomationConsole && (
                <SidebarItem
                  title="Commitments"
                  url={buildManagementUrl('commitments')}
                  disabled={!hasCommitmentClusters || anywhereOnly}
                  rules={[['view', 'OrganizationCommitments']]}
                  supportsDemo={false}
                  tooltip={commitmentsTooltip}
                />
              )}
              {!limitAutomationConsole && (
                <SidebarItem
                  title="Component control"
                  url={buildManagementUrl('component-control')}
                  rules={[
                    ['view', 'FeatureOrganizationManagementComponentControl'],
                  ]}
                />
              )}
              {!limitAutomationConsole && (
                <SidebarItem
                  title="Permissions center"
                  url={buildManagementUrl('permissions-center')}
                />
              )}
              {!hideDiscounts && (
                <SidebarItem
                  title="Price adjustments"
                  url={buildManagementUrl('discount-engine')}
                  rules={[['view', 'OrganizationDiscounts']]}
                  supportsDemo={false}
                />
              )}
            </SidebarSection>
          </>
        )}
      </>
    </SidebarLayout>
  );
};
